export interface BrushSettings {
  tipShape: "round" | "square" | "custom";
  size: number;
  hardness: number;
  spacing: number;
  sizeJitter: number;
  angleJitter: number;
  roundnessJitter: number;
  scatterAmount: number;
  countJitter: number;
  textureEnabled: boolean;
  texturePattern: string;
  dualBrushEnabled: boolean;
  fgBgJitter: number;
  hueJitter: number;
  opacityJitter: number;
  flowJitter: number;
  wetEdges: boolean;
  airbrushMode: boolean;
  smoothing: number;
}

export class BrushSettingsPanel {
  private container: HTMLElement;
  private settings: BrushSettings;
  private onChange: ((settings: BrushSettings) => void) | null = null;
  private presetList: { name: string; settings: Partial<BrushSettings> }[] = [];

  constructor() {
    this.settings = this._defaultSettings();
    this.container = document.createElement("div");
    this.container.className = "brush-settings-panel";
    this._buildUI();
    this._initPresets();
  }

  private _defaultSettings(): BrushSettings {
    return {
      tipShape: "round",
      size: 20,
      hardness: 100,
      spacing: 25,
      sizeJitter: 0,
      angleJitter: 0,
      roundnessJitter: 0,
      scatterAmount: 0,
      countJitter: 0,
      textureEnabled: false,
      texturePattern: "",
      dualBrushEnabled: false,
      fgBgJitter: 0,
      hueJitter: 0,
      opacityJitter: 0,
      flowJitter: 0,
      wetEdges: false,
      airbrushMode: false,
      smoothing: 25,
    };
  }

  private _initPresets(): void {
    this.presetList = [
      { name: "Hard Round", settings: { tipShape: "round", hardness: 100, sizeJitter: 0, spacing: 25 } },
      { name: "Soft Round", settings: { tipShape: "round", hardness: 0, sizeJitter: 0, spacing: 25 } },
      { name: "Hard Square", settings: { tipShape: "square", hardness: 100, sizeJitter: 0, spacing: 25 } },
      { name: "Soft Square", settings: { tipShape: "square", hardness: 0, sizeJitter: 0, spacing: 25 } },
      { name: "Scatter Brush", settings: { tipShape: "round", hardness: 50, scatterAmount: 100, countJitter: 50 } },
      { name: "Texture Brush", settings: { tipShape: "round", hardness: 80, textureEnabled: true } },
      { name: "Airbrush", settings: { tipShape: "round", hardness: 0, airbrushMode: true, smoothing: 50 } },
      { name: "Wet Edges", settings: { tipShape: "round", hardness: 50, wetEdges: true } },
      { name: "Dynamic Size", settings: { tipShape: "round", hardness: 80, sizeJitter: 50, smoothing: 25 } },
      { name: "Color Dynamics", settings: { tipShape: "round", hardness: 80, fgBgJitter: 30, hueJitter: 20 } },
    ];
  }

  private _buildUI(): void {
    this.container.innerHTML = `
      <div class="panel-title">Brush Settings</div>
      <div class="brush-settings__content">
        <div class="brush-presets">
          <select class="brush-presets-select" id="brush-preset-select">
            <option value="">— Presets —</option>
          </select>
        </div>
        <div class="brush-preview">
          <canvas id="brush-preview-canvas" width="100" height="100"></canvas>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Brush Tip Shape</div>
          <div class="brush-row">
            <label>Shape</label>
            <select id="brush-tip-shape">
              <option value="round">Round</option>
              <option value="square">Square</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div class="brush-row">
            <label>Size</label>
            <input type="range" min="1" max="500" value="20" id="brush-size" class="brush-slider">
            <span class="brush-val" id="brush-size-val">20</span>
          </div>
          <div class="brush-row">
            <label>Hardness</label>
            <input type="range" min="0" max="100" value="100" id="brush-hardness" class="brush-slider">
            <span class="brush-val" id="brush-hardness-val">100%</span>
          </div>
          <div class="brush-row">
            <label>Spacing</label>
            <input type="range" min="1" max="100" value="25" id="brush-spacing" class="brush-slider">
            <span class="brush-val" id="brush-spacing-val">25%</span>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Shape Dynamics</div>
          <div class="brush-row">
            <label>Size Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-size-jitter" class="brush-slider">
            <span class="brush-val" id="brush-size-jitter-val">0%</span>
          </div>
          <div class="brush-row">
            <label>Angle Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-angle-jitter" class="brush-slider">
            <span class="brush-val" id="brush-angle-jitter-val">0°</span>
          </div>
          <div class="brush-row">
            <label>Roundness Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-roundness-jitter" class="brush-slider">
            <span class="brush-val" id="brush-roundness-jitter-val">0%</span>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Scattering</div>
          <div class="brush-row">
            <label>Scatter</label>
            <input type="range" min="0" max="100" value="0" id="brush-scatter" class="brush-slider">
            <span class="brush-val" id="brush-scatter-val">0%</span>
          </div>
          <div class="brush-row">
            <label>Count Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-count-jitter" class="brush-slider">
            <span class="brush-val" id="brush-count-jitter-val">0%</span>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Texture</div>
          <div class="brush-row">
            <label><input type="checkbox" id="brush-texture"> Enable Texture</label>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Dual Brush</div>
          <div class="brush-row">
            <label><input type="checkbox" id="brush-dual"> Enable Dual Brush</label>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Color Dynamics</div>
          <div class="brush-row">
            <label>FG/BG Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-fg-bg-jitter" class="brush-slider">
            <span class="brush-val" id="brush-fg-bg-jitter-val">0%</span>
          </div>
          <div class="brush-row">
            <label>Hue Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-hue-jitter" class="brush-slider">
            <span class="brush-val" id="brush-hue-jitter-val">0°</span>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Transfer</div>
          <div class="brush-row">
            <label>Opacity Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-opacity-jitter" class="brush-slider">
            <span class="brush-val" id="brush-opacity-jitter-val">0%</span>
          </div>
          <div class="brush-row">
            <label>Flow Jitter</label>
            <input type="range" min="0" max="100" value="0" id="brush-flow-jitter" class="brush-slider">
            <span class="brush-val" id="brush-flow-jitter-val">0%</span>
          </div>
        </div>
        <div class="brush-section">
          <div class="brush-section__title">Options</div>
          <div class="brush-row"><label><input type="checkbox" id="brush-wet-edges"> Wet Edges</label></div>
          <div class="brush-row"><label><input type="checkbox" id="brush-airbrush"> Airbrush Mode</label></div>
          <div class="brush-row">
            <label>Smoothing</label>
            <input type="range" min="0" max="100" value="25" id="brush-smoothing" class="brush-slider">
            <span class="brush-val" id="brush-smoothing-val">25%</span>
          </div>
        </div>
      </div>
    `;

    this._bindEvents();
    this._populatePresets();
  }

  private _bindEvents(): void {
    const bind = (id: string, prop: keyof BrushSettings, parse: (v: string) => any, suffix: string = "") => {
      const el = this.container.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement;
      if (!el) return;
      el.addEventListener("input", () => {
        (this.settings as any)[prop] = parse(el.value);
        const valEl = this.container.querySelector(`#${id}-val`);
        if (valEl) valEl.textContent = el.value + suffix;
        this._updatePreview();
        this._emitChange();
      });
    };

    bind("brush-tip-shape", "tipShape", v => v as "round" | "square" | "custom");
    bind("brush-size", "size", v => parseInt(v));
    bind("brush-hardness", "hardness", v => parseInt(v), "%");
    bind("brush-spacing", "spacing", v => parseInt(v), "%");
    bind("brush-size-jitter", "sizeJitter", v => parseInt(v), "%");
    bind("brush-angle-jitter", "angleJitter", v => parseInt(v), "°");
    bind("brush-roundness-jitter", "roundnessJitter", v => parseInt(v), "%");
    bind("brush-scatter", "scatterAmount", v => parseInt(v), "%");
    bind("brush-count-jitter", "countJitter", v => parseInt(v), "%");
    bind("brush-fg-bg-jitter", "fgBgJitter", v => parseInt(v), "%");
    bind("brush-hue-jitter", "hueJitter", v => parseInt(v), "°");
    bind("brush-opacity-jitter", "opacityJitter", v => parseInt(v), "%");
    bind("brush-flow-jitter", "flowJitter", v => parseInt(v), "%");
    bind("brush-smoothing", "smoothing", v => parseInt(v), "%");

    const bindCheck = (id: string, prop: keyof BrushSettings) => {
      const el = this.container.querySelector(`#${id}`) as HTMLInputElement;
      if (!el) return;
      el.addEventListener("change", () => {
        (this.settings as any)[prop] = el.checked;
        this._updatePreview();
        this._emitChange();
      });
    };

    bindCheck("brush-texture", "textureEnabled");
    bindCheck("brush-dual", "dualBrushEnabled");
    bindCheck("brush-wet-edges", "wetEdges");
    bindCheck("brush-airbrush", "airbrushMode");

    const presetSelect = this.container.querySelector("#brush-preset-select") as HTMLSelectElement;
    if (presetSelect) {
      presetSelect.addEventListener("change", () => {
        const preset = this.presetList.find(p => p.name === presetSelect.value);
        if (preset) {
          Object.assign(this.settings, preset.settings);
          this._syncUI();
          this._updatePreview();
          this._emitChange();
        }
      });
    }
  }

  private _populatePresets(): void {
    const select = this.container.querySelector("#brush-preset-select") as HTMLSelectElement;
    if (!select) return;
    for (const preset of this.presetList) {
      const opt = document.createElement("option");
      opt.value = preset.name;
      opt.textContent = preset.name;
      select.appendChild(opt);
    }
  }

  private _syncUI(): void {
    const set = (id: string, value: string | number) => {
      const el = this.container.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement;
      if (el) el.value = String(value);
    };
    set("brush-tip-shape", this.settings.tipShape);
    set("brush-size", this.settings.size);
    set("brush-hardness", this.settings.hardness);
    set("brush-spacing", this.settings.spacing);
    set("brush-size-jitter", this.settings.sizeJitter);
    set("brush-angle-jitter", this.settings.angleJitter);
    set("brush-roundness-jitter", this.settings.roundnessJitter);
    set("brush-scatter", this.settings.scatterAmount);
    set("brush-count-jitter", this.settings.countJitter);
    set("brush-fg-bg-jitter", this.settings.fgBgJitter);
    set("brush-hue-jitter", this.settings.hueJitter);
    set("brush-opacity-jitter", this.settings.opacityJitter);
    set("brush-flow-jitter", this.settings.flowJitter);
    set("brush-smoothing", this.settings.smoothing);

    const setCheck = (id: string, value: boolean) => {
      const el = this.container.querySelector(`#${id}`) as HTMLInputElement;
      if (el) el.checked = value;
    };
    setCheck("brush-texture", this.settings.textureEnabled);
    setCheck("brush-dual", this.settings.dualBrushEnabled);
    setCheck("brush-wet-edges", this.settings.wetEdges);
    setCheck("brush-airbrush", this.settings.airbrushMode);

    const setVal = (id: string, text: string) => {
      const el = this.container.querySelector(`#${id}`);
      if (el) el.textContent = text;
    };
    setVal("brush-size-val", String(this.settings.size));
    setVal("brush-hardness-val", this.settings.hardness + "%");
    setVal("brush-spacing-val", this.settings.spacing + "%");
    setVal("brush-size-jitter-val", this.settings.sizeJitter + "%");
    setVal("brush-angle-jitter-val", this.settings.angleJitter + "°");
    setVal("brush-roundness-jitter-val", this.settings.roundnessJitter + "%");
    setVal("brush-scatter-val", this.settings.scatterAmount + "%");
    setVal("brush-count-jitter-val", this.settings.countJitter + "%");
    setVal("brush-fg-bg-jitter-val", this.settings.fgBgJitter + "%");
    setVal("brush-hue-jitter-val", this.settings.hueJitter + "°");
    setVal("brush-opacity-jitter-val", this.settings.opacityJitter + "%");
    setVal("brush-flow-jitter-val", this.settings.flowJitter + "%");
    setVal("brush-smoothing-val", this.settings.smoothing + "%");
  }

  private _updatePreview(): void {
    const canvas = this.container.querySelector("#brush-preview-canvas") as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const size = Math.min(this.settings.size, 80);
    const hardness = this.settings.hardness / 100;
    const cx = w / 2, cy = h / 2;

    if (this.settings.tipShape === "round") {
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
      gradient.addColorStop(0, "rgba(200,200,200,1)");
      gradient.addColorStop(hardness, "rgba(200,200,200,1)");
      gradient.addColorStop(1, "rgba(200,200,200,0)");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.settings.tipShape === "square") {
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
      gradient.addColorStop(0, "rgba(200,200,200,1)");
      gradient.addColorStop(hardness, "rgba(200,200,200,1)");
      gradient.addColorStop(1, "rgba(200,200,200,0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
    }
  }

  private _emitChange(): void {
    if (this.onChange) {
      this.onChange({ ...this.settings });
    }
  }

  setCallback(onChange: (settings: BrushSettings) => void): void {
    this.onChange = onChange;
  }

  getSettings(): BrushSettings {
    return { ...this.settings };
  }

  setSettings(settings: Partial<BrushSettings>): void {
    Object.assign(this.settings, settings);
    this._syncUI();
    this._updatePreview();
    this._emitChange();
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
    this._updatePreview();
  }

  destroy(): void {
    this.container.remove();
  }
}
